import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography, SxProps, Theme, useMediaQuery } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SaveBar, SectionCard } from '../../components/ui';
import { AdminAlertSettingsCard, AlertSettingsEmbedState } from '../../components/admin/AdminAlertSettingsCard';
import { useUnsavedChangesSource } from '../../components/appUpdate/unsavedChanges';
import { useToast } from '../../contexts/ToastContext';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { tokens } from '../../theme/tokens';
import { validateEmail, validatePhone } from '../../utils/inputValidators';
import { asDate, systemJson } from './api';

interface SupportContactSettings {
  dedicated: { name: string; email: string; phone?: string; hours?: string };
  standardEmail: string;
  updatedAt?: unknown;
  updatedBy?: string;
}

interface SupportContactsResponse {
  settings: SupportContactSettings | null;
  preview: { name: string; email: string; phone?: string; hours?: string };
}

interface BhSettings {
  settings: { maxConcurrentFills: number; updatedAt?: unknown; updatedBy?: string };
  defaults: { maxConcurrentFills: number; maxAllowed: number };
}

const fieldSx = (dirty: boolean): SxProps<Theme> =>
  dirty
    ? { '& .MuiOutlinedInput-root': { bgcolor: tokens.accent.tint, '& fieldset': { borderColor: tokens.accent.border } } }
    : {};

/** One labelled input in a fields column: the label sits above the box, as in the handoff. */
const Field: React.FC<{
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  dirty: boolean;
  error?: string | null;
  placeholder?: string;
  type?: string;
}> = ({ label, hint, value, onChange, dirty, error, placeholder, type }) => (
  <Box component="label" sx={{ display: 'flex', flexDirection: 'column', gap: 0.625, minWidth: 0 }}>
    <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 500, color: tokens.ink.body }}>
      {label}
      {hint && (
        <Box component="span" sx={{ color: tokens.ink.faint, fontWeight: 400 }}>
          {` · ${hint}`}
        </Box>
      )}
    </Typography>
    <TextField
      value={value}
      onChange={(event) => onChange(event.target.value)}
      size="small"
      fullWidth
      type={type}
      placeholder={placeholder}
      error={Boolean(error)}
      helperText={error || undefined}
      sx={fieldSx(dirty)}
    />
  </Box>
);

/** A row inside a settings card: what the setting is and who it affects, then its fields. */
const SettingRow: React.FC<{ label: string; help: string; divider?: boolean; children: React.ReactNode }> = ({ label, help, divider, children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '200px minmax(0, 1fr)' },
      gap: { xs: 1.5, md: '16px 24px' },
      px: '20px',
      py: '18px',
      borderTop: divider ? `1px solid ${tokens.line.hairline}` : 0,
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{help}</Typography>
    </Box>
    <Box sx={{ minWidth: 0, maxWidth: 640 }}>{children}</Box>
  </Box>
);

const fieldGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(auto-fit, minmax(220px, 1fr))' },
  gap: '12px 14px',
};

export const SettingsTab: React.FC<{ onDirty: (count: number, invalid: boolean) => void }> = ({ onDirty }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const stackMeta = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const support = useQuery({
    queryKey: ['admin-support-contacts'],
    queryFn: () => systemJson<SupportContactsResponse>('/billing/admin/support-contacts'),
  });
  const fills = useQuery({
    queryKey: ['admin-bh-fill-settings'],
    queryFn: () => systemJson<BhSettings>('/ingestion/bh-fill-settings'),
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [hours, setHours] = useState('');
  const [standardEmail, setStandardEmail] = useState('');
  const [concurrency, setConcurrency] = useState(1);
  const alertApi = useRef<AlertSettingsEmbedState | null>(null);
  const [alertDirty, setAlertDirty] = useState(false);
  const [alertInvalid, setAlertInvalid] = useState(false);
  const onAlertState = useCallback((state: AlertSettingsEmbedState | null) => {
    alertApi.current = state;
    setAlertDirty(state?.dirty ?? false);
    setAlertInvalid(state?.invalid ?? false);
  }, []);

  useEffect(() => {
    if (!support.data) return;
    const current = support.data.settings;
    setName(current?.dedicated.name ?? support.data.preview.name);
    setEmail(current?.dedicated.email ?? support.data.preview.email);
    setPhone(current?.dedicated.phone ?? support.data.preview.phone ?? '');
    setHours(current?.dedicated.hours ?? support.data.preview.hours ?? '');
    setStandardEmail(current?.standardEmail ?? '');
  }, [support.data]);
  useEffect(() => {
    if (fills.data) setConcurrency(fills.data.settings.maxConcurrentFills);
  }, [fills.data]);

  const savedSupport = {
    name: support.data?.settings?.dedicated.name ?? support.data?.preview.name ?? '',
    email: support.data?.settings?.dedicated.email ?? support.data?.preview.email ?? '',
    phone: support.data?.settings?.dedicated.phone ?? support.data?.preview.phone ?? '',
    hours: support.data?.settings?.dedicated.hours ?? support.data?.preview.hours ?? '',
    standardEmail: support.data?.settings?.standardEmail ?? '',
  };
  const supportChanged = support.data
    ? [name !== savedSupport.name, email !== savedSupport.email, phone !== savedSupport.phone, hours !== savedSupport.hours, standardEmail !== savedSupport.standardEmail].filter(Boolean).length
    : 0;
  const supportDirty = supportChanged > 0;
  const savedConcurrency = fills.data?.settings.maxConcurrentFills;
  const fillsDirty = savedConcurrency !== undefined && concurrency !== savedConcurrency;
  const dirtyCount = supportChanged + Number(fillsDirty) + Number(alertDirty);
  const emailError = validateEmail(email);
  const phoneError = validatePhone(phone);
  const standardError = validateEmail(standardEmail);
  const supportInvalid = !name.trim() || !email.trim() || !standardEmail.trim() || Boolean(emailError || phoneError || standardError);
  const invalid = supportInvalid || alertInvalid;

  useEffect(() => {
    onDirty(dirtyCount, invalid);
  }, [dirtyCount, invalid, onDirty]);

  const save = useMutation({
    mutationFn: async () => {
      if (supportDirty) {
        await systemJson('/billing/admin/support-contacts', {
          method: 'PUT',
          body: JSON.stringify({ dedicated: { name, email, phone: phone || undefined, hours: hours || undefined }, standardEmail }),
        });
      }
      let startedJobIds: string[] = [];
      if (fillsDirty) {
        const result = await systemJson<{ startedJobIds?: string[] }>('/ingestion/bh-fill-settings', {
          method: 'PUT',
          body: JSON.stringify({ maxConcurrentFills: concurrency }),
        });
        startedJobIds = result.startedJobIds || [];
      }
      if (alertApi.current?.dirty) await alertApi.current.save();
      return { startedJobIds };
    },
    onSuccess: (result) => {
      const started = result?.startedJobIds?.length || 0;
      showToast(started ? t('admin.systemV2.settings.savedWithJobs', { count: started }) : t('admin.systemV2.settings.saved'), 'success');
      queryClient.invalidateQueries({ queryKey: ['admin-support-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['admin-bh-fill-settings'] });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const discard = () => {
    if (support.data) {
      setName(savedSupport.name);
      setEmail(savedSupport.email);
      setPhone(savedSupport.phone);
      setHours(savedSupport.hours);
      setStandardEmail(savedSupport.standardEmail);
    }
    if (fills.data) setConcurrency(fills.data.settings.maxConcurrentFills);
    alertApi.current?.discard();
  };

  useUnsavedChangesSource({
    dirty: dirtyCount > 0,
    save: () => (invalid ? Promise.reject(new Error('invalid')) : save.mutateAsync()),
    discard,
  });

  const maxAllowed = fills.data?.defaults.maxAllowed ?? 10;
  const defaultConcurrency = fills.data?.defaults.maxConcurrentFills ?? 1;
  const concHelp = concurrency === 1 ? t('admin.systemV2.settings.concurrencyOne') : t('admin.systemV2.settings.concurrencyMany', { count: concurrency });
  const savedLine = (updatedAt: unknown, updatedBy: string | undefined) => {
    const when = asDate(updatedAt);
    if (!when) return t('admin.systemV2.settings.neverSaved');
    return t('admin.systemV2.settings.savedMeta', {
      when: when.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }),
      who: updatedBy || '—',
    });
  };
  // SectionCard's dense header is one row and its actions do not shrink, so on a
  // narrow screen the saved line moves under the heading instead of beside it.
  const cardHeader = (help: string, changed: number, updatedAt: unknown, updatedBy: string | undefined) => {
    const meta = (
      <Typography
        component="span"
        sx={{
          display: 'block',
          fontSize: 12,
          fontWeight: changed ? 600 : 400,
          color: changed ? tokens.accent.hover : tokens.ink.faint,
          textAlign: stackMeta ? 'left' : 'right',
          maxWidth: stackMeta ? undefined : 260,
          mt: stackMeta ? 0.5 : 0,
          overflowWrap: 'anywhere',
        }}
      >
        {changed ? t('admin.systemV2.settings.cardUnsaved', { count: changed }) : savedLine(updatedAt, updatedBy)}
      </Typography>
    );
    return stackMeta
      ? { subtitle: <>{help}{meta}</> }
      : { subtitle: help, actions: meta };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1080 }}>
      <SectionCard
        dense
        title={t('admin.systemV2.settings.contacts')}
        {...cardHeader(t('admin.systemV2.settings.contactsHelp'), supportChanged, support.data?.settings?.updatedAt, support.data?.settings?.updatedBy)}
      >
        <SettingRow label={t('admin.systemV2.settings.dedicated')} help={t('admin.systemV2.settings.dedicatedHelp')}>
          <Box sx={fieldGridSx}>
            <Field label={t('admin.systemV2.settings.name')} value={name} onChange={setName} dirty={name !== savedSupport.name} />
            <Field label={t('admin.systemV2.settings.email')} type="email" value={email} onChange={setEmail} dirty={email !== savedSupport.email} error={emailError} />
            <Field label={t('admin.systemV2.settings.phone')} hint={t('admin.systemV2.settings.optional')} value={phone} onChange={setPhone} dirty={phone !== savedSupport.phone} error={phoneError} />
            <Field
              label={t('admin.systemV2.settings.hours')}
              hint={t('admin.systemV2.settings.optional')}
              placeholder={t('admin.systemV2.settings.hoursPlaceholder')}
              value={hours}
              onChange={setHours}
              dirty={hours !== savedSupport.hours}
            />
          </Box>
        </SettingRow>
        <SettingRow divider label={t('admin.systemV2.settings.standard')} help={t('admin.systemV2.settings.standardHelp')}>
          <Box sx={fieldGridSx}>
            <Field
              label={t('admin.systemV2.settings.standardEmail')}
              type="email"
              value={standardEmail}
              onChange={setStandardEmail}
              dirty={standardEmail !== savedSupport.standardEmail}
              error={standardError}
            />
          </Box>
        </SettingRow>
      </SectionCard>

      <AdminAlertSettingsCard onState={onAlertState} />

      <SectionCard
        dense
        title={t('admin.systemV2.settings.fills')}
        {...cardHeader(t('admin.systemV2.settings.fillsHelp'), Number(fillsDirty), fills.data?.settings.updatedAt, fills.data?.settings.updatedBy)}
      >
        <SettingRow
          label={t('admin.systemV2.settings.concurrency')}
          help={t('admin.systemV2.settings.concurrencyRange', { max: maxAllowed, default: defaultConcurrency })}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
            <Box
              role="group"
              aria-label={t('admin.systemV2.settings.concurrency')}
              sx={{
                display: 'inline-flex',
                alignItems: 'stretch',
                border: `1px solid ${fillsDirty ? tokens.accent.border : tokens.line.strong}`,
                borderRadius: `${tokens.radius.small}px`,
                overflow: 'hidden',
                bgcolor: fillsDirty ? tokens.accent.tint : tokens.surface.card,
                '& .MuiButton-root': { minWidth: 36, height: 36, px: 0, borderRadius: 0, fontSize: 16, color: tokens.ink.bodyAlt },
              }}
            >
              <Button
                aria-label={t('admin.systemV2.settings.decrease')}
                disabled={concurrency <= 1}
                onClick={() => setConcurrency((value) => Math.max(1, value - 1))}
                sx={{ borderRight: `1px solid ${tokens.line.default}` }}
              >
                −
              </Button>
              <Box
                component="output"
                aria-live="polite"
                sx={{ minWidth: 52, display: 'grid', placeItems: 'center', fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              >
                {concurrency}
              </Box>
              <Button
                aria-label={t('admin.systemV2.settings.increase')}
                disabled={concurrency >= maxAllowed}
                onClick={() => setConcurrency((value) => Math.min(maxAllowed, value + 1))}
                sx={{ borderLeft: `1px solid ${tokens.line.default}` }}
              >
                +
              </Button>
            </Box>
            <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, maxWidth: 560 }}>{concHelp}</Typography>
          </Box>
        </SettingRow>
      </SectionCard>

      <SaveBar
        dirty={dirtyCount > 0}
        saving={save.isPending}
        saveDisabled={invalid}
        message={invalid ? (supportInvalid ? t('admin.systemV2.settings.invalid') : t('admin.alertSettings.invalid')) : t('admin.systemV2.settings.dirty', { count: dirtyCount })}
        onSave={() => save.mutate()}
        onDiscard={discard}
      />
    </Box>
  );
};
